import { useRouter } from "next/router";
import { CSSProperties, useEffect, useRef, useState } from "react";

/* 빠른 이동에서는 바가 깜빡이지 않도록 이 시간 이후에만 표시 */
const SHOW_DELAY_MS = 150;
const FINISH_MS = 200;
const FADE_MS = 300;

type Phase = "start" | "loading" | "done" | "fade";

/* transform만 애니메이션해서 레이아웃 계산 없이 부드럽게 움직임 */
const phaseStyle: Record<Phase, CSSProperties> = {
    start: { transform: "scaleX(0.08)", transition: "none" },
    /* 실제 진행률은 알 수 없으므로 90%까지 한 번에, 점점 느려지며 진행 */
    loading: {
        transform: "scaleX(0.9)",
        transition: "transform 8s cubic-bezier(0.1, 0.7, 0.3, 1)"
    },
    done: {
        transform: "scaleX(1)",
        transition: `transform ${FINISH_MS}ms ease-out`
    },
    fade: {
        transform: "scaleX(1)",
        opacity: 0,
        transition: `opacity ${FADE_MS}ms ease-out`
    }
};

/**
 * 페이지 이동(getServerSideProps 대기 포함) 중 상단에 표시되는 진행바
 */
export const RouteProgressBar = () => {
    const router = useRouter();
    const [phase, setPhase] = useState<Phase | null>(null);

    const phaseRef = useRef<Phase | null>(null);
    const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
    const frame = useRef<number>(undefined);

    useEffect(() => {
        const update = (next: Phase | null) => {
            phaseRef.current = next;
            setPhase(next);
        };

        const clearTimers = () => {
            timers.current.forEach(clearTimeout);
            timers.current = [];
            if (frame.current) cancelAnimationFrame(frame.current);
            frame.current = undefined;
        };

        const handleStart = (
            _url: string,
            { shallow }: { shallow: boolean }
        ) => {
            if (shallow) return;
            /* 이미 진행 중이면 이어서 진행 */
            if (phaseRef.current === "start" || phaseRef.current === "loading")
                return;
            if (timers.current.length && phaseRef.current === null) return;

            /* 이전 바가 사라지는 중이면 100% → 8%로 줄어드는 대신 바로 숨김 */
            clearTimers();
            update(null);

            timers.current = [
                setTimeout(() => {
                    update("start");
                    /* 시작 위치가 그려진 다음 프레임에 전환을 시작해야 애니메이션됨 */
                    frame.current = requestAnimationFrame(() => {
                        frame.current = requestAnimationFrame(() =>
                            update("loading")
                        );
                    });
                }, SHOW_DELAY_MS)
            ];
        };

        const handleDone = () => {
            const wasShown =
                phaseRef.current === "start" || phaseRef.current === "loading";
            clearTimers();
            if (!wasShown) {
                update(null);
                return;
            }

            update("done");
            timers.current = [
                setTimeout(() => update("fade"), FINISH_MS),
                setTimeout(() => update(null), FINISH_MS + FADE_MS)
            ];
        };

        router.events.on("routeChangeStart", handleStart);
        router.events.on("routeChangeComplete", handleDone);
        router.events.on("routeChangeError", handleDone);

        return () => {
            router.events.off("routeChangeStart", handleStart);
            router.events.off("routeChangeComplete", handleDone);
            router.events.off("routeChangeError", handleDone);
            clearTimers();
        };
    }, [router.events]);

    if (phase === null) return null;

    return (
        <div
            role="progressbar"
            aria-label="페이지 로딩 중"
            className="pointer-events-none fixed left-0 top-0 z-[1001] h-[3px]
                w-full origin-left bg-black will-change-transform dark:bg-white"
            style={phaseStyle[phase]}
        />
    );
};
